import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';
import { Radio, ChevronDown, ChevronRight, Mail, Phone, MapPin, Pause, Instagram, Facebook } from 'lucide-react';
import { LanguageSelector } from '@/components/LanguageSelector';
import InstallPWAButton from '@/components/InstallPWAButton';
import ArticleCard from '@/components/ArticleCard';
import FeaturedArticleBanner from '@/components/FeaturedArticleBanner';
import { isMobileApp } from '@/utils/mobileBridge';
import { useAudioStore } from '@/hooks/useAudioStore';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { DEFAULT_LIVE_STATION } from '@/constants/stations';
import {
  ITEMS_PER_PAGE,
  type NewsItem,
} from '@/constants/news';
import { fetchNews, fetchProgram, fetchArticles, fetchStreams } from '@/services/strapi';
import { maybeTransliterate } from '@/utils/transliterate';
import type { RadioStation } from '@/types';

/** TikTok logo – jednostavan SVG */
const TikTokIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SectionWrapper: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const SUTON_MAIN_FALLBACK: RadioStation = DEFAULT_LIVE_STATION;

/** Pronađi najbolji match za "SUTON Radio LIVE" iz svježe Strapi liste. */
const findSutonLive = (streams: RadioStation[]): RadioStation | null => {
  return (
    streams.find((s) => s.streamUrl?.includes('suton_radio')) ??
    streams.find((s) => s.id === 'suton-main') ??
    streams.find((s) => /suton.*live|live.*suton/i.test(s.name ?? '')) ??
    null
  );
};

const paginate = <T,>(items: T[], page: number) => {
  const start = (page - 1) * ITEMS_PER_PAGE;
  return items.slice(start, start + ITEMS_PER_PAGE);
};

export const NewsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isPlaying } = useAudioStore();
  const { playStation, togglePlayPause } = useAudioPlayer();
  const [newsPage, setNewsPage] = useState(1);
  const [programPage, setProgramPage] = useState(1);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [articles, setArticles] = useState<NewsItem[]>([]);
  const [programItems, setProgramItems] = useState<NewsItem[]>([]);
  const [sutonLive, setSutonLive] = useState<RadioStation>(SUTON_MAIN_FALLBACK);
  const vestiSectionRef = useRef<HTMLDivElement>(null);
  const programSectionRef = useRef<HTMLDivElement>(null);

  const locale = i18n.resolvedLanguage || i18n.language || 'de';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [news, program, articleList, streamList] = await Promise.all([
        fetchNews(locale),
        fetchProgram(locale),
        fetchArticles(locale),
        fetchStreams(locale),
      ]);
      if (cancelled) return;
      setArticles(articleList);

      // TASK 26/27: hardcoded mock vesti i programi više ne postoje. Ako Strapi
      // vrati prazno, state ostaje prazan i NewsScreen pokazuje empty state.
      setNewsItems(news);
      setProgramItems(program);

      // FIX-PACK: "Slušaj" dugme uvijek koristi NAJNOVIJI SUTON Radio LIVE objekat
      // iz Strapi-ja (artwork, name, color, metadata) umjesto hardkodiranog
      // STATIONS konstante. Fallback ostaje SUTON_MAIN_FALLBACK ako Strapi prazan.
      const fresh = findSutonLive(streamList);
      if (fresh) setSutonLive(fresh);

      if (import.meta.env.DEV) {
        console.log('[NewsScreen] fetchArticles returned:', articleList.length, 'items');
        const featured = articleList.find((a) => a.featured);
        console.log('[NewsScreen] Featured article:', featured ?? '(none — set featured:true in Strapi to enable banner)');
      }
    })();
    return () => { cancelled = true; };
  }, [locale]);

  // TASK 24: featured Article ide u banner iznad grida; regularni Articles
  // (bez `featured` flag-a) se mixaju sa legacy `news-items` u jednoj listi
  // koja se paginira zajedno. Ako Strapi nema Articles, ostaje samo newsItems.
  const featuredArticle: NewsItem | undefined = articles.find((a) => a.featured);
  const regularArticles: NewsItem[] = articles.filter((a) => !a.featured);
  const combinedNews: NewsItem[] =
    articles.length > 0 ? [...regularArticles, ...newsItems] : newsItems;
  const newsPages = Math.ceil(combinedNews.length / ITEMS_PER_PAGE);
  const paginatedNews = paginate(combinedNews, newsPage);

  const featuredProgram: NewsItem | undefined = programItems.find((p) => p.featured);
  const regularPrograms: NewsItem[] = programItems.filter((p) => !p.featured);
  const programPages = Math.ceil(regularPrograms.length / ITEMS_PER_PAGE);
  const paginatedProgram = paginate(regularPrograms, programPage);

  const handleSlusaj = () => {
    if (isPlaying) {
      togglePlayPause();
    } else {
      playStation(sutonLive);
    }
  };

  const handleNewsPageChange = (page: number) => {
    setNewsPage(page);
    vestiSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleProgramPageChange = (page: number) => {
    setProgramPage(page);
    programSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="overflow-x-hidden">
      {/* Hero - full viewport, dark bg-home */}
      <section className="min-h-screen relative flex flex-col justify-between">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat news-hero-parallax"
          style={{ backgroundImage: 'url(/bg-home.png)' }}
        />
        {/* Plavi overlay umjesto sivog/crnog – gornji dio ostaje plav, ne siv */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(13, 27, 42, 0.65) 0%, rgba(13, 27, 42, 0.4) 50%, transparent 100%)',
          }}
          aria-hidden
        />
        <div className="relative z-10 px-4 sm:px-8 lg:px-16 pt-8 sm:pt-12">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative flex items-center justify-between"
          >
            <motion.div
              onClick={handleSlusaj}
              className={`inline-flex rounded-xl cursor-pointer transition-all ${
                isPlaying ? 'animate-gradient-tab shadow-lg shadow-purple-500/30 p-1.5' : ''
              }`}
            >
              <img
                src="/logo-suton-news.png"
                alt="SUTON Radio"
                className="h-16 sm:h-20 object-contain object-left rounded-lg"
              />
            </motion.div>
            <div className="absolute left-1/2 -translate-x-1/2">
              <LanguageSelector />
            </div>
            <button
              onClick={handleSlusaj}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                isPlaying
                  ? 'animate-gradient-tab shadow-lg shadow-purple-500/30 text-white'
                  : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
            >
              {isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Radio className="w-4 h-4" />
              )}
              {t('news.listen')}
            </button>
          </motion.div>
        </div>
        <div className="relative z-10 px-4 sm:px-8 lg:px-16 flex-1 flex flex-col items-center justify-center text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-4 max-w-4xl"
          >
            {t('news.heroTitle')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto"
          >
            {t('news.heroSubtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              onClick={() => navigate('/app')}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white transition-all hover:scale-105 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, #E64A8E 0%, #6A1A9A 100%)',
                boxShadow: '0 10px 40px rgba(230, 74, 142, 0.4)',
              }}
            >
              <Radio className="w-5 h-5" />
              {t('news.discoverStreams')}
              <ChevronRight className="w-5 h-5" />
            </button>

            <InstallPWAButton />
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="relative z-10 flex justify-center pb-12"
        >
          <ChevronDown className="w-8 h-8 text-white/60 animate-bounce" />
        </motion.div>
      </section>

      {/* Mi smo SUTON Radio */}
      <SectionWrapper className="relative py-20 sm:py-28 px-4 sm:px-8 lg:px-16 overflow-hidden">
        {/* Veći raster i nježniji prelaz */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-60"
          style={{
            backgroundImage: 'url(/bg-smoke.png)',
            backgroundSize: '180%',
          }}
          aria-hidden
        />
        <div className="relative z-10 max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8">{t('news.weAre')}</h2>
          <p className="text-lg text-text-secondary leading-relaxed mb-6">
            {t('news.weAreText1')}
          </p>
          <p className="text-lg text-text-secondary leading-relaxed mb-6">
            {t('news.weAreText2')}
          </p>
          <p className="text-xl font-semibold text-white">
            {t('news.weAreText3')}
          </p>
        </div>
      </SectionWrapper>

      {/* Vesti & Aktuelno - svijetla podloga, 6 po stranici, slike 16:9 obavezne */}
      <SectionWrapper className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16 bg-white">
        <div ref={vestiSectionRef} className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900">{t('news.newsTitle')}</h2>
          <p className="text-lg text-gray-600 mb-12">
            {t('news.newsSubtitle')}
          </p>
          {featuredArticle && newsPage === 1 && (
            <FeaturedArticleBanner article={featuredArticle} />
          )}
          {combinedNews.length === 0 && !featuredArticle ? (
            <div className="text-center py-16 px-6 rounded-2xl bg-gray-50 border border-gray-100">
              <p className="text-text-muted text-lg">{t('news.empty')}</p>
            </div>
          ) : (
            <>
              {combinedNews.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {paginatedNews.map((item: NewsItem) => (
                    <ArticleCard
                      key={item.id}
                      type="article"
                      id={item.id}
                      slug={item.slug}
                      title={item.title}
                      excerpt={item.excerpt || item.description}
                      image={item.image}
                      publishedAt={item.publishedAt}
                      variant="light"
                    />
                  ))}
                </div>
              )}
              {newsPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: newsPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => handleNewsPageChange(p)}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        newsPage === p
                          ? 'bg-accent-pink text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/app')}
            className="mt-8 flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-white"
            style={{
              background: 'linear-gradient(135deg, #6A1A9A 0%, #E64A8E 100%)',
            }}
          >
            <Radio className="w-4 h-4" />
            {t('news.listenRadio')}
          </motion.button>
        </div>
      </SectionWrapper>

      {/* Naš program – 6 po stranici, slike 16:9 obavezne */}
      <SectionWrapper className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16">
        <div ref={programSectionRef} className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t('news.programTitle')}</h2>
          <p className="text-lg text-text-secondary mb-12">
            {t('news.programSubtitle')}
          </p>
          {featuredProgram && programPage === 1 && (
            <FeaturedArticleBanner article={featuredProgram} type="program" />
          )}
          {programItems.length === 0 ? (
            <div className="text-center py-16 px-6 rounded-2xl glass border border-white/10">
              <p className="text-text-muted text-lg">{t('program.empty')}</p>
            </div>
          ) : (
            <>
              {regularPrograms.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {paginatedProgram.map((item: NewsItem) => (
                    <ArticleCard
                      key={item.id}
                      type="program"
                      id={item.id}
                      slug={item.slug}
                      title={item.title}
                      excerpt={item.excerpt || item.description}
                      image={item.image}
                      publishedAt={item.publishedAt}
                      variant="glass"
                    />
                  ))}
                </div>
              )}
              {programPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: programPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => handleProgramPageChange(p)}
                      className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                        programPage === p
                          ? 'bg-accent-pink text-white'
                          : 'glass hover:bg-white/10 text-text-secondary'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </SectionWrapper>

      {/* Naš Team */}
      <SectionWrapper className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-gray-900">{t('news.teamTitle')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Logo kao prva slika – 6. stavka za ravnomjeran raspored */}
            <div className="rounded-2xl bg-gray-50 border border-gray-100 overflow-hidden flex flex-col items-center justify-center p-6">
              <div className="aspect-[3/4] w-full max-w-[200px] mx-auto overflow-hidden flex items-center justify-center rounded-lg">
                <img
                  src="/logo-radio-suton.png"
                  alt="SUTON Radio"
                  className="w-full h-full object-contain p-4"
                />
              </div>
              <div className="p-4 text-center">
                <h3 className="font-semibold text-gray-900">{t('news.ourTeam')}</h3>
              </div>
            </div>
            {[
              { name: 'Jasmina Regeljac', role: t('news.roles.director'), image: '/team-jasmina-regeljac.jpg' },
              { name: 'Nataša Marković', role: t('news.roles.hostSocial'), image: '/team-natasa-markovic.jpg' },
              { name: 'Zoran Vicelarević', role: t('news.roles.journalist'), image: '/team-zoran-vicelarevic.jpg' },
              { name: 'Bojan Regeljac', role: t('news.roles.editor'), image: '/team-bojan-regeljac.jpg' },
              { name: 'Miloš Regeljac', role: t('news.roles.socialJournalist'), image: '/team-milos-regeljac.jpg' },
            ].map((person) => (
              <div
                key={person.name}
                className="rounded-2xl bg-gray-50 border border-gray-100 overflow-hidden"
              >
                <div className="aspect-[3/4] w-full max-w-[200px] mx-auto overflow-hidden">
                  <img
                    src={person.image}
                    alt={person.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-4 text-center">
                  <h3 className="font-semibold text-gray-900">{maybeTransliterate(person.name, locale)}</h3>
                  <p className="text-sm text-gray-600">{person.role}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Preuzmi aplikaciju (sakriveno unutar native WebView — Apple Guideline 4.2) */}
          {!isMobileApp() && (
            <div className="mt-16 pt-12 border-t border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">{t('news.downloadApp')}</h3>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="https://apps.apple.com/de/app/suton-radio/id6769689707"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-[140px] h-[46px] sm:w-[160px] sm:h-[54px] overflow-hidden shrink-0 hover:opacity-90 transition-opacity"
                  aria-label="Preuzmi na App Store"
                >
                  <img
                    src="/badge-apple.png"
                    alt={t('news.downloadApp')}
                    className="w-full h-full object-contain"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=de.sutonradio.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-[140px] h-[46px] sm:w-[160px] sm:h-[54px] overflow-hidden shrink-0 hover:opacity-90 transition-opacity"
                  aria-label={t('news.downloadApp')}
                >
                  <img
                    src="/badge-android.png"
                    alt={t('news.downloadApp')}
                    className="w-full h-full object-contain"
                  />
                </a>
              </div>
            </div>
          )}

          {/* Društvene mreže */}
          <div className="mt-10 pt-10 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">{t('news.followUs')}</h3>
            <div className="flex justify-center gap-6">
              <a
                href="https://www.instagram.com/sutonradio"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 hover:bg-pink-100 text-gray-700 hover:text-pink-600 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
              <a
                href="https://www.tiktok.com/@sutonradio?_r=1&_t=ZG-93U1SpJkS7V"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 hover:bg-black text-gray-700 hover:text-white transition-colors"
                aria-label="TikTok"
              >
                <TikTokIcon className="w-6 h-6" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61586958675557&locale=de_DE"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 hover:bg-blue-600 text-gray-700 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
      </SectionWrapper>

      {/* Naši partneri – rotirajući scroll logotipa */}
      <SectionWrapper className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900 text-center">{t('news.partnersTitle')}</h2>
          <p className="text-gray-600 mb-12 text-center max-w-2xl mx-auto">
            {t('news.partnersSubtitle')}
          </p>
          <div className="relative">
            <div className="overflow-hidden">
              <div className="flex animate-partner-scroll w-max">
                {[...Array(2)].map((_, setIdx) => (
                  <div key={setIdx} className="flex items-center gap-12 sm:gap-16 px-8 shrink-0">
                    {[
                      { src: '/partner-bmw.png', alt: 'BMW Frankfurt', url: 'https://www.bmw-frankfurt.de/de/home/' },
                      { src: '/partner-mini.png', alt: 'MINI Frankfurt', url: 'https://www.frankfurt.mini/de/home/' },
                      { src: '/partner-audi.png', alt: 'Audi / Autoschmitt Idstein', url: 'https://www.autoschmitt-idstein.audi/de/' },
                      { src: '/partner-dak.jpg', alt: 'DAK Gesundheit', url: 'https://mitgliedwerden.dak.de/prweb/PRAuth/app/DAKOST/S8o1wUJA7iJFnkCy2E8wfw*/!STANDARD?pzuiactionrrr=CXtpbn0xNFdwSExBVlBYeU9Ba0RYR2FvN2MyNWlPVExOTmZhWlAwYTNzRFYzY3FITk9LVVNGemhNTmRKZk1uZVZncm9aYXBkN3lqUnU1bjk3YWlNZ3JJRGVvRUNUd0hENGhwek45eGlBSFpDR2xmaG4zbXk4YXQ2c1A4YlphVmZtYkllTHRRK0x3cFlneG1wTnU3TG1mdXJiQXNWbGN0YVBRMWt2dThjajFKaHRSQlU9*' },
                      { src: '/partner-ergo.png', alt: 'ERGO', url: 'https://www.ergo.de/' },
                      { src: '/partner-eam.jpg', alt: 'EAM', url: 'https://www.suton-radio.de/' },
                      { src: '/partner-elements.jpg', alt: '5 Elements', url: 'https://5elements-sports.com/' },
                      { src: '/partner-gastarbajter24.jpg', alt: 'Gastarbajter24', url: 'https://www.gastarbajter24.de/' },
                      { src: '/partner-commerzbank.png', alt: 'Commerzbank', url: 'https://www.commerzbank.de/' },
                    ].map((logo) => (
                      <a
                        key={`${setIdx}-${logo.alt}`}
                        href={logo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-24 h-16 sm:w-28 sm:h-20 shrink-0 opacity-90 hover:opacity-100 transition-opacity duration-300 cursor-pointer"
                        aria-label={logo.alt}
                      >
                        <img
                          src={logo.src}
                          alt={logo.alt}
                          className="max-w-full max-h-full object-contain"
                        />
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </SectionWrapper>

      {/* Zabava i dobro raspoloženje */}
      <SectionWrapper className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">{t('news.entertainmentTitle')}</h2>
          <p className="text-lg text-text-secondary leading-relaxed mb-8">
            {t('news.entertainmentText1')}
          </p>
          <p className="text-text-secondary">
            {t('news.entertainmentText2')}
          </p>
        </div>
      </SectionWrapper>

      {/* Kontakt */}
      <SectionWrapper className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-gray-900">{t('news.contactTitle')}</h2>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-stretch">
            {/* Lijevo: podaci (na mobilnom iznad) */}
            <div className="flex flex-col justify-center lg:w-2/5 lg:min-w-[240px] space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-accent-pink flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-gray-900">{t('news.address')}</p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Zeilweg+17-19,+60439+Frankfurt+am+Main"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-accent-pink block"
                  >
                    {maybeTransliterate('Zeilweg 17-19', locale)}
                  </a>
                  <p className="text-gray-600">{maybeTransliterate('60439 Frankfurt am Main', locale)}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Phone className="w-6 h-6 text-accent-pink flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">{t('news.phone')}</p>
                  <a href="tel:+4969271339100" className="text-gray-600 hover:text-accent-pink">
                    +49 (0) 69 271 339 100
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Mail className="w-6 h-6 text-accent-pink flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">{t('news.email')}</p>
                  <a
                    href="mailto:info@suton-radio.de"
                    className="text-gray-600 hover:text-accent-pink block"
                  >
                    {maybeTransliterate('info@suton-radio.de', locale)}
                  </a>
                  <a
                    href="mailto:werbung@suton-radio.de"
                    className="text-gray-600 hover:text-accent-pink block"
                  >
                    {maybeTransliterate('werbung@suton-radio.de', locale)}
                  </a>
                </div>
              </div>
            </div>

            {/* Desno: mapa (na mobilnom ispod) */}
            <div className="flex-1 lg:min-w-0 rounded-2xl overflow-hidden border border-gray-200 shadow-lg aspect-video min-h-[280px] lg:min-h-[320px]">
              <iframe
                title="SUTON Radio – Zeilweg 17-19, Frankfurt am Main"
                src="https://maps.google.com/maps?q=Zeilweg%2017-19%2C%2060439%20Frankfurt%20am%20Main&z=16&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: 280 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full min-h-[280px]"
              />
            </div>
          </div>
        </div>
      </SectionWrapper>

      {/* CTA footer */}
      <section className="py-16 px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center"
        >
          <p className="text-lg text-text-secondary mb-6">
            {t('news.ctaText')}
          </p>
          <button
            onClick={() => navigate('/app')}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #E64A8E 0%, #6A1A9A 100%)',
              boxShadow: '0 10px 40px rgba(230, 74, 142, 0.4)',
            }}
          >
            <Radio className="w-5 h-5" />
            {t('news.listenSuton')}
          </button>
        </motion.div>
      </section>

      {/* Footer – Impressum, Datenschutz, Copyright */}
      <footer className="py-8 px-4 sm:px-8 lg:px-16 border-t border-white/10">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-sm text-text-muted">
          <Link
            to="/impressum"
            className="hover:text-white transition-colors"
          >
            {t('footer.impressum')}
          </Link>
          <Link
            to="/datenschutz"
            className="hover:text-white transition-colors"
          >
            {t('footer.datenschutz')}
          </Link>
          <span className="text-text-muted">{t('footer.copyright')}</span>
        </div>
      </footer>
    </div>
  );
};
